'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';
import styles from './VideoJS.module.css';

interface VideoJsOptions {
  autoplay: boolean;
  controls: boolean;
  responsive: boolean;
  fluid: boolean;
  sources: {
    src: string;
    type: string;
  }[];
  playsinline?: boolean;
  html5?: {
    vhs?: {
      overrideNative: boolean;
    };
    nativeAudioTracks: boolean;
    nativeVideoTracks: boolean;
  };
}

interface VideoJSProps {
  options: VideoJsOptions;
  onReady?: (player: Player) => void;
  onPlay?: (player: Player) => void;
  onPause?: (player: Player) => void;
  closeMobileVid?: () => void;
}


export const VideoJS = (props: VideoJSProps) => {
  const t = useTranslations('homepage');
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const { options, onReady, onPlay, closeMobileVid } = props;
  const { width } = useWindowSize();

  // State for custom controls
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [, setIsFullscreen] = useState(false); //isFullscreen is not used, for now as it is disabled
  const hideControlsTimeout = useRef<NodeJS.Timeout | null>(null);
  // Format time helper
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show controls temporarily
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    if (isPlaying) {
      hideControlsTimeout.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  // Control handlers
  const handlePlayPause = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    if (player.paused()) {
      player.play();
    } else {
      player.pause();
    }
  }, []);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const player = playerRef.current;
      if (!player) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      const playerDuration = player.duration() ?? 0;
      player.currentTime(pos * playerDuration);
    },
    [],
  );

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const player = playerRef.current;
      if (!player) return;

      const newVolume = parseFloat(e.target.value);
      setVolume(newVolume);
      player.volume(newVolume);

      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    },
    [isMuted],
  );

  const handleMuteToggle = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    if (isMuted) {
      player.muted(false);
      setIsMuted(false);
      if (volume === 0) {
        player.volume(0.5);
        setVolume(0.5);
      }
    } else {
      player.muted(true);
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  // const handleFullscreen = useCallback(() => {
  //   const player = playerRef.current;
  //   if (!player) return;

  //   if (player.isFullscreen()) {
  //     player.exitFullscreen();
  //   } else {
  //     player.requestFullscreen();
  //   }
  // }, []);

  // Skip function kept for future use if needed
  // const handleSkip = useCallback((seconds: number) => {
  //   const player = playerRef.current;
  //   if (!player) return;

  //   const currentTime = player.currentTime() ?? 0;
  //   const duration = player.duration() ?? 0;
  //   const newTime = currentTime + seconds;
  //   player.currentTime(Math.max(0, Math.min(newTime, duration)));
  // }, []);

  useEffect(() => {
    if (!width || playerRef.current) return;

    try {
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered', 'center-on-desk');

      if (!videoRef.current) return;
      videoRef.current.appendChild(videoElement);

      const finalOptions = {
        ...options,
        autoplay: width > 992 ? true : options.autoplay,
        playsinline: true,
        html5: {
          vhs: { overrideNative: true },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
      };

      const player = videojs(videoElement, finalOptions, () => {
        videojs.log('player is ready');
        onReady?.(player);
        setDuration(player.duration() || 0);
        setVolume(player.volume() ?? 1);
        setIsMuted(player.muted() ?? false);
      });

      playerRef.current = player;

      // Set up event listeners for custom controls
      player.on('play', () => {
        setIsPlaying(true);
        onPlay?.(player);
        showControlsTemporarily();
      });

      player.on('pause', () => {
        setIsPlaying(false);
        setShowControls(true);
        if (hideControlsTimeout.current) {
          clearTimeout(hideControlsTimeout.current);
        }

        if (width < 992) {
          try {
            if (player.isFullscreen()) {
              player.exitFullscreen().catch((error: any) => {
                console.log('Exit fullscreen failed:', error);
              });
            }
          } catch (error) {
            console.log('Exit fullscreen error:', error);
          }
        }
      });

      player.on('timeupdate', () => {
        setCurrentTime(player.currentTime() ?? 0);

        // Update buffered
        const buffered = player.buffered();
        const playerDuration = player.duration() ?? 1;
        const bufferedEnd =
          buffered && buffered.length > 0
            ? buffered.end(buffered.length - 1)
            : 0;
        setBuffered((bufferedEnd / playerDuration) * 100);
      });

      player.on('loadedmetadata', () => {
        setDuration(player.duration() ?? 0);
      });

      player.on('volumechange', () => {
        setVolume(player.volume() ?? 1);
        setIsMuted(player.muted() ?? false);
      });

      player.on('fullscreenchange', () => {
        const fullscreen = player.isFullscreen() ?? false;
        setIsFullscreen(fullscreen);
        if (!fullscreen) {
          closeMobileVid?.();
        }
      });

      player.on('error', (error: any) => {
        console.error('Video.js error:', error);
      });

      return () => {
        if (player && !player.isDisposed()) {
          player.dispose();
          playerRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error initializing video player:', error);
    }
  }, [width]);

  // Update source when options change
  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }
  }, [options.sources, options.autoplay]);

  // Cleanup controls timeout on unmount
  useEffect(() => {
    return () => {
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, []);

  return (
    <div
      className={`${styles.videoContainer} video-player`}
      data-vjs-player
      onMouseMove={showControlsTemporarily}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      role="region"
      aria-label={t('homepage.videoJS.aria.videoPlayer')}
    >
      <div ref={videoRef} />

      {/* Center Play Button */}
      <button
        className={styles.centerPlayButton}
        data-visible={!isPlaying ? 'true' : 'false'}
        onClick={handlePlayPause}
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        <svg viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      </button>

      {/* Custom Controls */}
      <div
        className={styles.controlsOverlay}
        data-visible={showControls || !isPlaying ? 'true' : 'false'}
      >
        <div className={styles.controlBar}>
          {/* Progress Bar and Time Display */}
          <div className={styles.progressBarRow}>
            <div
              className={styles.progressBarContainer}
              onClick={handleProgressClick}
              role="slider"
              aria-label={t('homepage.videoJS.aria.videoProgress')}
              aria-valuemin={0}
              aria-valuemax={duration}
              aria-valuenow={currentTime}
              aria-valuetext={`${formatTime(currentTime)} of ${formatTime(
                duration,
              )}`}
            >
              <div
                className={styles.bufferedBar}
                style={{ '--bb-buffered': buffered } as React.CSSProperties}
              />
              <div
                className={styles.progressBar}
                style={
                  {
                    '--pb-progress': (currentTime / duration) * 100 || 0,
                  } as React.CSSProperties
                }
              />
            </div>
            <span className={styles.timeDisplay}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Controls Row */}
          <div className={styles.controlsRow}>
            {/* Left Controls */}
            <div className={styles.controlsGroup}>
              <button
                className={`${styles.controlButton} ${styles.playButton}`}
                onClick={handlePlayPause}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <svg viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Skip buttons commented out - keeping function available if needed */}
              {/* <ControlButton
                onClick={() => handleSkip(-5)}
                aria-label="Rewind 5 seconds"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
                  <text
                    x="12"
                    y="15"
                    fontSize="8"
                    fill="white"
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    5
                  </text>
                </svg>
              </ControlButton>

              <ControlButton
                onClick={() => handleSkip(5)}
                aria-label="Forward 5 seconds"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z" />
                  <text
                    x="12"
                    y="15"
                    fontSize="8"
                    fill="white"
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    5
                  </text>
                </svg>
              </ControlButton> */}

              <div className={styles.volumeContainer}>
                <button
                  className={styles.controlButton}
                  onClick={handleMuteToggle}
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted || volume === 0 ? (
                    <svg viewBox="0 0 24 24">
                      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                    </svg>
                  ) : volume < 0.5 ? (
                    <svg viewBox="0 0 24 24">
                      <path d="M7 9v6h4l5 5V4l-5 5H7z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                    </svg>
                  )}
                </button>

                <input
                  className={styles.volumeSlider}
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  style={{ '--vol': isMuted ? 0 : volume } as React.CSSProperties}
                  aria-label={t('homepage.videoJS.aria.volume')}
                  aria-valuemin={0}
                  aria-valuemax={1}
                  aria-valuenow={isMuted ? 0 : volume}
                  aria-valuetext={`Volume ${Math.round(
                    (isMuted ? 0 : volume) * 100,
                  )}%`}
                />
              </div>
            </div>

            {/* Right Controls */}
            {/* <ControlsGroup>
              <ControlButton
                onClick={handleFullscreen}
                aria-label={
                  isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
                }
              >
                {isFullscreen ? (
                  <svg viewBox="0 0 24 24">
                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24">
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                  </svg>
                )}
              </ControlButton>
            </ControlsGroup> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoJS;
